/**
 * One copilot turn's stream, folded into the shape the panel renders.
 *
 * React-free on purpose, and the only module in this feature that is. Everything
 * here is a pure function over a frame, which is what lets `scripts/verify-copilot-live.mjs`
 * drive it against a real backend with no DOM — and this is the part of the client
 * most likely to be subtly wrong and least likely to fail loudly, because a merge
 * rule that is off by one frame produces a plausible answer rather than an error.
 *
 * ── The frames are DOUBLE-WRAPPED, and this is the thing to know ──────────
 * The server writes `event: content` with a data line of `{"type":"content","data":{…}}`
 * — the type twice, and the payload nested. `parseSseFrame` spreads the parsed
 * JSON, so a caller receives `{event, type, data}` and every field it wants is at
 * `event.data.*`. A reducer that reads `event.text` — which is what this client
 * did until now — finds `undefined` on every frame and renders an empty answer
 * with no error anywhere. Renaming the events alone does not fix it.
 *
 * ── Items merge by `id`; they do not append ───────────────────────────────
 * `think.steps` and `content.blocks` are accumulated server-side by item id
 * (`app/core/realtime/copilot.py`), and the engine relies on it: every delta of
 * the answer is sent as the SAME block, id `answer`, and the text CONCATENATES.
 * A client that appends instead renders one paragraph per delta. `mergeById`
 * below mirrors the server's rule exactly, so what is held here equals the row
 * the backend persists — which is what makes a replayed turn and a live one the
 * same thing.
 */

/** Overwritten rather than concatenated. Mirrors the server's `_FIXED_ITEM_KEYS`. */
const FIXED_KEYS = new Set(['id', 'type', 'title'])

/**
 * The accumulation contract, mirrored from `app/core/realtime/copilot.py`.
 *
 * Same id → merge field by field: string onto string CONCATENATES, a list extends,
 * anything else overwrites, and `id`/`type`/`title` always overwrite. New id →
 * append. Never merge by position: starts and ends interleave freely and the same
 * tool can run twice in one turn.
 */
export function mergeById(items, incoming) {
  if (!Array.isArray(incoming) || incoming.length === 0) return items
  const next = items.map((item) => ({ ...item }))
  const at = new Map(next.map((item, index) => [item.id, index]))

  for (const item of incoming) {
    if (!item || typeof item !== 'object') continue
    const index = at.get(item.id)
    if (index === undefined) {
      at.set(item.id, next.length)
      next.push({ ...item })
      continue
    }
    const existing = next[index]
    for (const [key, value] of Object.entries(item)) {
      if (FIXED_KEYS.has(key)) existing[key] = value
      else if (typeof existing[key] === 'string' && typeof value === 'string') existing[key] += value
      else if (Array.isArray(existing[key]) && Array.isArray(value)) existing[key] = [...existing[key], ...value]
      else existing[key] = value
    }
  }
  return next
}

/**
 * How long a step took, out of the string the engine reports it in.
 *
 * `tool_end` sends `description: "842ms"` against the same id its start sent with
 * an empty one, so this is the field that says a step has finished. Anything that
 * is not a duration — an empty description, or the RCA step, which carries
 * `status: "running"` and no description at all — is `null`, meaning "still
 * running as far as this frame is concerned".
 */
export function parseStageMs(description) {
  if (typeof description !== 'string') return null
  const match = description.match(/^\s*(\d+(?:\.\d+)?)\s*ms\s*$/)
  return match ? Number(match[1]) : null
}

/**
 * One `think` step as the dock's stage strip holds it.
 *
 * `name` falls back to the id because the id is always present and a nameless row
 * in the strip is worse than a technical one. `done` is carried separately from
 * `ms` rather than derived from it at render time, and that distinction is
 * load-bearing: the RCA step announces itself as `{title: "Analysing", status:
 * "running"}` and is NEVER updated to a terminal value, so an analysis that
 * finished would keep a live indicator for ever. The `done` frame closes it (see
 * `reduceTurnEvent`), and it closes with no duration — which the strip has to be
 * able to render, because `ms: 0` would put "0ms" on screen against a step that
 * took six minutes.
 */
export function stageFromStep(step) {
  const ms = parseStageMs(step?.description)
  return {
    id: step?.id ?? '',
    name: step?.title || step?.id || '',
    ms,
    done: ms != null,
  }
}

/**
 * The blocks as the one string every reader in this app already renders.
 *
 * The dock's reveal, its quotable region, its caret and its disclaimer all key off
 * a single `text`, and none of them should change in a pass this large. Joined on
 * a blank line because separate blocks are separate paragraphs — today the engine
 * sends exactly one for an ordinary answer and one for an analysis, so the join is
 * insurance rather than a feature.
 *
 * Block-level rendering is a later change, wanted only when a block type other
 * than `text` appears. None does today.
 */
export function textOf(blocks) {
  return (blocks ?? [])
    .map((block) => (typeof block?.text === 'string' ? block.text : ''))
    .filter(Boolean)
    .join('\n\n')
}

/** A turn before its first frame. Every key present, so a live turn and a replayed one match. */
export const emptyTurnState = {
  steps: [],
  blocks: [],
  stages: [],
  text: '',
  sources: [],
  actions: [],
  error: null,
  finished: false,
}

/**
 * Fold one frame onto a turn, as a patch to merge into it.
 *
 * Returns `null` for a frame that changes nothing, so a caller can skip the render
 * rather than setting state to an equal value on every keepalive.
 *
 * Both the raw items and what is derived from them are returned. The duplication is
 * deliberate: the raw `steps` and `blocks` are what the NEXT frame merges against —
 * they have to survive — while `stages` and `text` are what the panel reads, and
 * deriving those at render time would mean every consumer re-implementing the
 * accumulation rule.
 */
export function reduceTurnEvent(state, event) {
  /* `type` is the inner truth and `event` is the SSE frame name. They agree on
     every ordinary frame and the two synthetic errors prove they need not, so the
     inner one wins where there is one. */
  switch (event?.type ?? event?.event) {
    case 'think': {
      const steps = mergeById(state.steps ?? [], event.data?.steps)
      if (steps === state.steps) return null
      return { steps, stages: steps.map(stageFromStep) }
    }

    case 'content': {
      const blocks = mergeById(state.blocks ?? [], event.data?.blocks)
      if (blocks === state.blocks) return null
      return { blocks, text: textOf(blocks) }
    }

    /* Replaced, not merged. Sources are sent once, at the end, and only when there
       are any — so a second frame carrying them is a correction rather than an
       addition. */
    case 'metadata':
      if (!event.data?.sources) return null
      return { sources: event.data.sources }

    /**
     * The terminal, and the only place two things can be learnt.
     *
     * `data.action` is what the server's action dispatcher actually DID —
     * `{module, type, module_id, status, reason}` — and it exists nowhere else.
     * It is not persisted onto the turn, so a reader who missed this frame can
     * never find out what a turn wrote. That is why the caller over-invalidates
     * on a turn that requested an analysis rather than trusting this list.
     *
     * And every open stage closes here. See `stageFromStep`.
     */
    case 'done':
      return {
        actions: event.data?.action ?? [],
        finished: true,
        stages: (state.stages ?? []).map((stage) => (stage.done ? stage : { ...stage, done: true })),
      }

    /**
     * The one frame that is read outside `data`.
     *
     * Two errors carry no `data` key at all: the sweep's synthetic timeout, and
     * `stream unavailable` from attaching to a turn the server has already reaped.
     * Both are the cases a client most needs to recognise, so both are read.
     */
    case 'error':
      return {
        error: event.data?.message ?? event.message ?? 'unknown',
        finished: true,
      }

    default:
      return null
  }
}
